import type { TradeItem } from '@/types';
import {
  MTM_SYNTHETIC_COLLATERAL_DELTA,
  MTM_SYNTHETIC_NEW_MARGIN,
  MTM_SYNTHETIC_VALUE_DELTA,
} from '@/stores/trades/mtm-column-store';
import {
  computeCollateralDelta,
  computeMtmRow,
  computeRepoMarginValueDelta,
  computeRepoValueDelta,
  computeValueDelta,
  mtmBaselinePriceRaw,
} from './mtm-calculations';
import { isMtmEligibleRow, isRepoCallableInScope, type RepoCallScope } from './mtm-cell-helpers';

/**
 * The synthetic MTM columns that carry a column total (LR-538 / demo feedback п.60):
 * New Margin Value, Gross Value Δ and Margin Value Δ. Last Marked and New Price are
 * per-share prices, and Gross Price Δ is a per-share move — none of them sums.
 *
 * These three can't live in `SUMMABLE_COLUMNS` (csv-totals.ts): every accessor there
 * takes a bare `TradeItem`, whereas these figures depend on the operator's unsaved
 * New Price overrides. Hence the separate pre-aggregation below, folded into the same
 * `describeCurrencyTotal` display path by the footer and the CSV export.
 */
export const MTM_SYNTHETIC_SUMMABLE_IDS: readonly string[] = [
  MTM_SYNTHETIC_NEW_MARGIN,
  MTM_SYNTHETIC_VALUE_DELTA,
  MTM_SYNTHETIC_COLLATERAL_DELTA,
];

/**
 * The two synthetic columns that measure a MOVE from a per-product baseline, and are
 * therefore not addable across products (see `buildMtmSyntheticCcyBuckets`):
 * stock L/B measures from the moving last mark and feeds Run MTM, repo/reverse from the
 * fixed booking price and feeds the cash-pool call. `New Margin Value` is deliberately
 * NOT here — `qty × new_price × margin` has no baseline and no direction, so it means
 * the same thing for both products, exactly like `Trade Margin Value`, which has summed
 * across both since LR-548.
 */
const MTM_SYNTHETIC_DELTA_IDS: readonly string[] = [
  MTM_SYNTHETIC_VALUE_DELTA,
  MTM_SYNTHETIC_COLLATERAL_DELTA,
];

/** columnId → { CCY → native sum }. Consumed by `aggregateFromBuckets`. */
export type MtmSyntheticCcyBuckets = Record<string, Record<string, number>>;

/** Result of the accumulator: the per-column buckets plus why a delta column may be empty. */
export interface MtmSyntheticTotals {
  byColumn: MtmSyntheticCcyBuckets;
  /** True when BOTH a stock-L/B and a repo/reverse row contributed, so the two delta
   *  columns come back as `{}` on purpose. Callers surface the reason. */
  deltasMixedProducts: boolean;
}

const addTo = (bucket: Record<string, number>, ccy: string, value: number): void => {
  if (!Number.isFinite(value)) return;
  bucket[ccy] = (bucket[ccy] ?? 0) + value;
};

/** The three figures behind one row, in its SECURITY currency. */
export interface MtmSyntheticRowValues {
  newMargin: number;
  valueDelta: number;
  marginValueDelta: number;
}

/**
 * The three override-dependent synthetic figures for ONE row, or `null` when the row
 * contributes nothing and its cells render "—": ineligible status, a repo row outside
 * the margin call (`isRepoCallableInScope` — the same predicate the checkbox, the
 * footer scope and the export basis use), or an unparseable price/baseline.
 *
 * Single source of truth for that branch set, shared by the footer accumulator below
 * and the CSV row pass. Both used to mirror `MtmTable`'s cells by hand, and any drift
 * showed up as a footer (or a file) disagreeing with the rows it was built from —
 * the failure mode LR-548 called out when it un-gated repo on this grid.
 *
 * Repo/reverse MUST go through the repo helpers: `computeValueDelta` and
 * `computeCollateralDelta` route through `pnlDirection`, which is 0 for repo (repos
 * are not MtM'd — LR-385), so reusing them would silently yield zero while the cells
 * show non-zero figures. The repo baseline is the FIXED booking price
 * (`gross_price_raw`), not the moving `mtmBaselinePriceRaw` — repos are never
 * repriced. `newMargin` needs no split: `computeMtmRow.recomputedMargin` is
 * direction-agnostic (qty × price × margin) and correct for both products.
 */
export function mtmSyntheticRowValues(
  item: TradeItem,
  override: string | undefined,
  repoCall?: RepoCallScope,
): MtmSyntheticRowValues | null {
  const isRepoType = item.trade_type === 'repo' || item.trade_type === 'reverse_repo';
  // Must track `eligibleIds` / `footerRows`, which key off the EFFECTIVE status: gating on the
  // raw status here would keep a row in the totals' basis yet contribute null — a silent
  // under-count of the footer and CSV, with no row visibly missing.
  if (!isMtmEligibleRow(item)) return null;
  if (isRepoType && !isRepoCallableInScope(item, repoCall)) return null;

  const mtm = computeMtmRow(item, override);
  const effectivePrice = Number(mtm.effectivePrice);
  const baselinePrice = Number(isRepoType ? item.gross_price_raw : mtmBaselinePriceRaw(item));
  if (Number.isNaN(effectivePrice) || Number.isNaN(baselinePrice)) return null;

  return {
    newMargin: Number(mtm.recomputedMargin),
    valueDelta: isRepoType
      ? computeRepoValueDelta(item, effectivePrice, baselinePrice)
      : computeValueDelta(item, effectivePrice, baselinePrice),
    marginValueDelta: isRepoType
      ? computeRepoMarginValueDelta(item, effectivePrice, baselinePrice)
      : computeCollateralDelta(item, effectivePrice, baselinePrice),
  };
}

/**
 * The same three figures keyed by COLUMN ID and blanked (`''`) for a row that shows
 * "—" on screen, which is the shape the CSV row pass writes. Keeps the id↔figure
 * pairing next to the accumulator that totals the very same numbers, so a file's rows
 * can't drift from its own totals block.
 */
export function mtmSyntheticRowCells(
  item: TradeItem,
  override: string | undefined,
  repoCall?: RepoCallScope,
  invalidIds?: Set<number>,
): Record<string, number | ''> {
  const v = invalidIds?.has(item.id) ? null : mtmSyntheticRowValues(item, override, repoCall);
  return {
    [MTM_SYNTHETIC_NEW_MARGIN]: v ? v.newMargin : '',
    [MTM_SYNTHETIC_VALUE_DELTA]: v ? v.valueDelta : '',
    [MTM_SYNTHETIC_COLLATERAL_DELTA]: v ? v.marginValueDelta : '',
  };
}

/**
 * Per-currency native sums for the three override-dependent synthetic MTM columns.
 *
 * Bucket currency is the SECURITY ccy (`item.currency`) — the currency these cells
 * actually print. This deliberately differs from the real money columns, which bucket
 * by settlement ccy via `SUMMABLE_BUCKET_CCY`: these figures are computed from the
 * raw New Price the operator typed and are never run through the booked FX twin, so
 * filing them under a settlement label would mislabel the money.
 *
 * A column with no contributing row at all comes back as `{}` — callers must render
 * that as an EMPTY footer/CSV cell, not as a base-currency zero, since every cell in
 * the column above it shows "—".
 *
 * **The two delta columns are not summed across products** (kvi r3 #1). With no bucket
 * filter — the default view of a counterparty holding both GMSLA and GMRA — the scope
 * carries markable stock rows AND callable repo rows, and their deltas are different
 * quantities: stock is `pnlDirection × (new − last mark) × qty`, the P&L effect of the
 * marks Run MTM would post; repo is `repoExposureDirection × (new − booking) × qty`, the
 * cash-pool exposure change a margin call would settle, and repo never enters the run at
 * all. Their sum matches no action a dealer can take, while looking exactly like a figure
 * that does — `Margin Value Δ` over repo rows is the PRICE part of the per-line exposure the
 * call is computed from (the backend's `clean_exposure`; under Method B the call also carries
 * unpaid accrued repo interest, which stays out of this column — LR-579). So when both
 * populations contribute, both delta columns come back `{}`
 * and `deltasMixedProducts` is set; one product in scope (either bucket selected, or a
 * single-product counterparty on its default view) totals as usual. Deliberately decided
 * HERE rather than in the footer, so the on-screen totals and the CSV totals block — which
 * share this accumulator — cannot disagree. Per-ROW cells are unaffected: they keep
 * printing each row's own delta, matching the grid.
 *
 * @param rows            already scoped by the caller to the rows in the run/call
 *                        (`footerRows` on screen, the export scope in the CSV).
 * @param priceOverrides  shared New Price overrides, keyed by position id — applies to
 *                        repo rows too (only the SELECTION set is separate for repo).
 * @param repoCall        the counterparty's margin-call scope (currency + whether it is
 *                        unresolvable); gates repo rows through the same
 *                        `isRepoCallableInScope` the cells and the checkbox use.
 * @param invalidIds      rows whose New Price input is currently invalid — their cells
 *                        render "—", so they must not contribute.
 */
export function buildMtmSyntheticCcyBuckets(
  rows: TradeItem[],
  priceOverrides: Map<number, string>,
  repoCall?: RepoCallScope,
  invalidIds?: Set<number>,
): MtmSyntheticTotals {
  const newMargin: Record<string, number> = {};
  const valueDelta: Record<string, number> = {};
  const marginValueDelta: Record<string, number> = {};
  // Only rows that actually CONTRIBUTED count as a population present: an off-currency
  // repo row or a row with an invalid price is not in any total, so it must not suppress
  // the stock totals around it.
  let stockContributed = false;
  let repoContributed = false;

  for (const item of rows) {
    if (invalidIds?.has(item.id)) continue;
    const v = mtmSyntheticRowValues(item, priceOverrides.get(item.id), repoCall);
    if (v === null) continue;

    if (item.trade_type === 'repo' || item.trade_type === 'reverse_repo') repoContributed = true;
    else stockContributed = true;

    const ccy = (item.currency || '').toUpperCase();
    addTo(newMargin, ccy, v.newMargin);
    addTo(valueDelta, ccy, v.valueDelta);
    addTo(marginValueDelta, ccy, v.marginValueDelta);
  }

  const byColumn: MtmSyntheticCcyBuckets = {
    [MTM_SYNTHETIC_NEW_MARGIN]: newMargin,
    [MTM_SYNTHETIC_VALUE_DELTA]: valueDelta,
    [MTM_SYNTHETIC_COLLATERAL_DELTA]: marginValueDelta,
  };
  const deltasMixedProducts = stockContributed && repoContributed;
  // One list drives the suppression, so "which columns are baseline-relative" is stated once.
  if (deltasMixedProducts) for (const id of MTM_SYNTHETIC_DELTA_IDS) byColumn[id] = {};

  return { byColumn, deltasMixedProducts };
}
